import { Loader, ShoppingCart } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { can_visit, getPractice, notify } from '../../../helpers';
import { useDocumentTitle } from '../../../hooks/customDocumentTitleHook';
import Spacing from '../../Spacing';
import { oneTimeCheckoutPackage } from '../../../services/billing';
import StatusViewModal from '../../Modal/StatusViewModal';
import { useLocation } from 'react-router-dom';
import FormViewDetailsModal from '../../Modal/FormViewDetailsModal';
import { Config } from '../../../Config';

export default function Credits({selectedPracticeChange, fetchChangePracticeData, smsLimit, letterLimit}) {
  let account_id = getPractice("account_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    account_id = selectedPracticeChange?.account_id;
  }

  const smsQuantiity = Config.SMSCreditQuantity;
  const [paymentStatus, setPaymentStatus] = useState(false);
  const [hasCheckedStatus, setHasCheckedStatus] = useState(false);
  const [formViewId, setFormViewId] = useState(null);
  const [formView, setFormView] = useState(false);
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsInformations, setSmsInformations] = useState({
    account_id: account_id,
    credit: 0,
    quantity: 0,
    type: 'message_credits'
  });

  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);
  const letterQuantiity = Config.LetterCreditQuantity;
  const [letterLoading, setLetterLoading] = useState(false);
  const [letterInformations, setLetterInformations] = useState({
    account_id: account_id,
    available_credit: 0,
    quantity: 0,
    type: 'letter_credits'
  });

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const subscriptionSession = queryParams.get('session_id');
  const subscriptionPaymentStatus = queryParams.get('payment');
  useEffect(() => {
    if (subscriptionSession !== null && !hasCheckedStatus) {
      setPaymentStatus(true);
      setHasCheckedStatus(true);
      setPreviousScrollPosition(window.scrollY);
      window.scrollTo(0, 0);
    }
  }, [subscriptionSession, hasCheckedStatus]);

  const handleFormView = (formId) => {
    setFormViewId(formId)
    setFormView(true);
    setPreviousScrollPosition(window.scrollY);
    window.scrollTo(0, 0);
  };

  const substrateSmsCredit = () => {
    setSmsInformations((prevState) => {
      const newCredit = Math.max(prevState.credit - smsQuantiity, 0);
      const newQuantity = Math.floor(newCredit / smsQuantiity);
      return { ...prevState, credit: newCredit, quantity: newQuantity };
    });
  };

  const addMoreSmsCredit = () => {
    setSmsInformations((prevState) => {
      const newCredit = prevState.credit + smsQuantiity;
      const newQuantity = Math.floor(newCredit / smsQuantiity);
      return { ...prevState, credit: newCredit, quantity: newQuantity };
    });
  };

  const substrateLetterCredit = () => {
    setLetterInformations((prevState) => {
      const newCredit = Math.max(prevState.available_credit - letterQuantiity, 0);
      const newQuantity = Math.floor(newCredit / letterQuantiity);
      return { ...prevState, available_credit: newCredit, quantity: newQuantity };
    });
  };

  const addMoreLetterCredit = () => {
    setLetterInformations((prevState) => {
      const newCredit = prevState.available_credit + letterQuantiity;
      const newQuantity = Math.floor(newCredit / letterQuantiity);
      return { ...prevState, available_credit: newCredit, quantity: newQuantity };
    });
  };

  const handleSmsSubmit = async (e) => {
    e.preventDefault();
    e.target.disabled = true;
    setSmsLoading(true);

    const { credit, ...buyCredit } = smsInformations;
    if(can_visit("settings") && can_visit("settings_message_credits")) {
      try {
        const result = await oneTimeCheckoutPackage(buyCredit);

        if(result){
          if(result?.data?.payment_status === "unpaid" && result?.data?.status === "open"){
            window.location.href = result?.data?.url;
          } else {
            notify("error", result?.detail);
          }
        }
      } catch (error) {
        if (error?.response && error?.response?.data && error?.response?.data?.detail) {
          notify("error", error?.response?.data?.detail, 0.5);
        }
      } finally {
        setSmsInformations({
          ...smsInformations,
          credit: 0,
          quantity: 0,
        });
        setSmsLoading(false);
        e.target.disabled = false;
      }
    }
  };

  const handleLetterSubmit = async (e) => {
    e.preventDefault();
    e.target.disabled = true;
    setLetterLoading(true);

    const { available_credit, ...buyCredit } = letterInformations;

    if(can_visit("settings") && can_visit("settings_letter_credits")) {
      try {
        const result = await oneTimeCheckoutPackage(buyCredit);

        if(result){
          if(result?.data?.payment_status === "unpaid" && result?.data?.status === "open"){
            window.location.href = result?.data?.url;
          } else {
            notify("error", result?.detail);
          }
        }
      } catch (error) {
        if (error?.response && error?.response?.data && error?.response?.data?.detail) {
          notify("error", error?.response?.data?.detail, 0.5);
        }
      } finally {
        setLetterInformations({
          ...letterInformations,
          available_credit: 0,
          quantity: 0,
        });
        setLetterLoading(false);
        e.target.disabled = false;
      }
    }
  };

  useEffect(() => {
    setSmsInformations({
      ...smsInformations,
      account_id: account_id,
    });
    setLetterInformations({
      ...letterInformations,
      account_id: account_id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account_id]);

  const totalSMSCost = (smsInformations?.credit / smsQuantiity) * 10;
  const totalLetterCost = (letterInformations.available_credit / letterQuantiity) * 10;
  
  useDocumentTitle("Credits");

  return (
    <>
      <div className="docpro_grid docpro_grid_gap_20_16 docpro_grid_col_2">
        {(can_visit("settings") && can_visit("settings_message_credits")) && (
          <div className="docpro_grid_item">
            <div className='docpro_card'>
              <Spacing lg="5" sm="5" />
              <h3 className="docpro_fs_24 docpro_mb_0">Buy Message Credits</h3>
              <Spacing lg="18" sm="18" />

              <div className="docpro_funfact_1">
                <div className="docpro_funfact_item">
                  <p className='docpro_fs_16'>Your Available Message Credit</p>
                  <div className='flex items-center'>
                    <h3>{(smsLimit && smsLimit?.available_credit) ? smsLimit?.available_credit : 0}</h3>
                    {smsInformations?.credit > 0 ? 
                      <>
                        <span className='px-3'>+</span> <h3 style={{ color:"#057f05" }}>{smsInformations?.credit}</h3>
                        <span className='px-3'>=</span> <h3>{(smsLimit && smsLimit?.available_credit) ? smsLimit?.available_credit + smsInformations?.credit : smsInformations?.credit}</h3>
                      </> 
                    :
                      ""
                    }
                  </div>
                </div>
              </div>
              <div className='docpro_credit_wrap'>
                <form onSubmit={handleSmsSubmit} className="w-full">
                  <h2 className='docpro_fs_16 docpro_mb_0'>Message Credit: $10 ({smsQuantiity} credits)</h2>
                  <Spacing lg="10" sm="10" />
                  <div className="docpro_credit_input">
                    <p className='docpro_fs_16 docpro_mb_0 mr-2'>How many credits would you like to buy? </p>
                    <div className="docpro_quantity">
                      <button type="button" onClick={substrateSmsCredit} disabled={smsInformations?.credit === 0}>
                        <svg width={16} height={4} viewBox="0 0 16 4" fill="none" xmlns="http://www.w3.org/2000/svg" >
                          <path d="M2.16602 2H13.8327" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      <span>{ smsInformations? smsInformations?.credit : 0 }</span>
                      <button type="button" onClick={addMoreSmsCredit}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" >
                          <path d="M7.99935 2.16602V13.8327M2.16602 7.99935H13.8327" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <Spacing lg="20" sm="20" />

                  <div className='docpro_credit_card'>
                    <h2 className='docpro_fs_18 docpro_mb_0'>Order Summary</h2>
                    <Spacing lg="15" sm="15" />
                    <div className="docpro_table_1 hipaa_table" style={{ boxShadow: 'none', borderRadius: '5px' }}>
                      <table className="docpro_mb_0" style={{ width: '100%' }}>
                        <tbody>
                          <tr>
                            <td className='docpro_fs_14 docpro_fw_500'>DocPro Bulk Message</td>
                            <td className="text-right docpro_fs_14 docpro_fw_500" style={{ width: '100px' }}>{smsInformations?.quantity}</td>
                            <td className="text-right docpro_fs_14 docpro_fw_500" style={{ width: '150px' }}>
                              {smsInformations?.credit} credits
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={3}><hr /></td>
                          </tr>
                          <tr>
                            <td colSpan={2} className='docpro_fs_14 docpro_fw_500'>Sub Total</td>
                            <td className="text-right docpro_fs_14 docpro_fw_500">${totalSMSCost?.toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td colSpan={2} className='docpro_ternary_color'>Discount</td>
                            <td className="text-right docpro_ternary_color">-$0.00</td>
                          </tr>
                          <tr>
                            <td colSpan={3}><hr /></td>
                          </tr>
                          <tr>
                            <td colSpan={2} className='docpro_fs_14 docpro_fs_14 docpro_fw_500'>Total</td>
                            <td className="text-right docpro_fs_14 docpro_fw_500">${totalSMSCost?.toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {can_visit("settings_message_credits") && (
                    <>
                      <Spacing lg="8" sm="8" />
                      <button
                        type="submit"
                        className={`docpro_bordered_btn docpro_fw_600 size_lg docpro_filed_btn ${smsInformations?.credit === 0 ? "docpro_disabled" : ""}`}
                        style={{ pointerEvents: `${smsLoading ? "none" : "initial"}` }}
                        disabled={smsInformations?.credit === 0}
                      >
                        <ShoppingCart width={20} height={20} />
                        Buy Credit Now
                        {smsLoading && (
                          <span className="docpro_loading">
                            <Loader width={20} height={20} />
                          </span>
                        )}
                      </button>
                    </>
                  )}
                </form>
              </div>
              <Spacing lg="8" sm="8" />
            </div>
          </div>
        )}
          
        {(can_visit("settings") && can_visit("settings_letter_credits")) && (
          <div className="docpro_grid_item">
            <div className='docpro_card'>
              <Spacing lg="5" sm="5" />
              <h3 className="docpro_fs_24 docpro_mb_0">Buy Letter Credits</h3>
              <Spacing lg="18" sm="18" />

              <div className="docpro_funfact_1">
                <div className="docpro_funfact_item">
                  <p className='docpro_fs_16'>Your Available Letter Credit</p>
                  <div className='flex items-center'>
                    <h3>{(letterLimit && letterLimit.available_credit) ? letterLimit.available_credit : 0}</h3>
                    {letterInformations.available_credit > 0 ? 
                      <>
                        <span className='px-3'>+</span> <h3 style={{ color:"#057f05" }}>{letterInformations.available_credit}</h3>
                        <span className='px-3'>=</span> <h3>{(letterLimit && letterLimit.available_credit) ? letterLimit.available_credit + letterInformations.available_credit : letterInformations.available_credit}</h3>
                      </> 
                      : ""
                    }
                  </div>
                </div>
              </div>

              <div className="docpro_credit_wrap">
                <form onSubmit={handleLetterSubmit} className='w-full'>
                  <h2 className='docpro_fs_16 docpro_mb_0'>Letter Credit: $10 ({letterQuantiity} credits)</h2>
                  <Spacing lg="10" sm="10" />
                  <div className='docpro_credit_input'>
                    <p className='docpro_fs_16 docpro_mb_0 mr-2'>How many credits would you like to buy? </p>
                    <div className="docpro_quantity">
                      <button type="button" onClick={substrateLetterCredit} disabled={letterInformations.available_credit === 0}>
                        <svg width={16} height={4} viewBox="0 0 16 4" fill="none" xmlns="http://www.w3.org/2000/svg" >
                          <path d="M2.16602 2H13.8327" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      <span>{ letterInformations? letterInformations.available_credit : 0 }</span>
                      <button type="button" onClick={addMoreLetterCredit}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" >
                          <path d="M7.99935 2.16602V13.8327M2.16602 7.99935H13.8327" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <Spacing lg="20" sm="20" />

                  <div className='docpro_credit_card'>
                    <h2 className='docpro_fs_18 docpro_mb_0'>Order Summary</h2>
                    <Spacing lg="15" sm="15" />
                    <div className="docpro_table_1 hipaa_table" style={{ boxShadow: 'none', borderRadius: '5px' }}>
                      <table className="docpro_mb_0" style={{ width: '100%' }}>
                        <tbody>
                          <tr>
                            <td className='docpro_fs_14 docpro_fw_500'>DocPro Bulk Letter</td>
                            <td className="text-right docpro_fs_14 docpro_fw_500" style={{ width: '100px' }}>{letterInformations?.quantity}</td>
                            <td className="text-right docpro_fs_14 docpro_fw_500" style={{ width: '150px' }}>
                              {letterInformations?.credit} credits
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={3}><hr /></td>
                          </tr>
                          <tr>
                            <td colSpan={2} className='docpro_fs_14 docpro_fw_500'>Sub Total</td>
                            <td className="text-right docpro_fs_14 docpro_fw_500">${totalLetterCost?.toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td colSpan={2} className='docpro_ternary_color'>Discount</td>
                            <td className="text-right docpro_ternary_color">-$0.00</td>
                          </tr>
                          <tr>
                            <td colSpan={3}><hr /></td>
                          </tr>
                          <tr>
                            <td colSpan={2} className='docpro_fs_14 docpro_fs_14 docpro_fw_500'>Total</td>
                            <td className="text-right docpro_fs_14 docpro_fw_500">${totalLetterCost?.toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {can_visit("settings_letter_credits") && (
                    <>
                      <Spacing lg="8" sm="8" />
                      <button
                        type="submit"
                        className={`docpro_bordered_btn docpro_fw_600 size_lg docpro_filed_btn ${letterInformations.available_credit === 0 ? "docpro_disabled" : ""}`}
                        style={{ pointerEvents: `${letterLoading ? "none" : "initial"}` }}
                        disabled={letterInformations.available_credit === 0}
                      >
                        <ShoppingCart width={20} height={20} />
                        Buy Credit Now
                        {letterLoading && (
                          <span className="docpro_loading">
                            <Loader width={20} height={20} />
                          </span>
                        )}
                      </button>
                    </>
                  )}
                </form>
              </div>
              <Spacing lg="8" sm="8" />
            </div>
          </div>
        )}
      </div>

      {paymentStatus && (
        <StatusViewModal
          openModal={paymentStatus}
          setOpenModal={setPaymentStatus}
          session={subscriptionSession}
          account_id={account_id}
          payment_type={subscriptionPaymentStatus}
          handleFormView={handleFormView}
          previousScrollPosition={previousScrollPosition}
        />
      )}

      {formView && (
        <FormViewDetailsModal
          formId = {formViewId}
          formSize = {'A4'}
          formView = {formView}
          setFormView = {setFormView}
          formType = {'invoice'}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}